import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {KycRecord} from "../domain/kyc.entity";
import {CreateKycParams} from "../domain/kyc.types";
import {createKycRepository} from "../data/repositories";
import {AnalyticsEvent} from "../../../utils/analytics";
import {KYC_QUERY_KEYS, LEGACY_KYC_QUERY_KEY} from "./useKycStatus";
import {PROFILE_STATUS_QUERY_KEY} from "../../profile-status/hooks";

/**
 * Mutation hook for creating a new KYC record.
 * Used by useSubmitKyc orchestration hook after documents are uploaded.
 *
 * Fires analytics events and invalidates both new and legacy cache keys.
 */
export const useCreateKyc = () => {
  const queryClient = useQueryClient();
  const repository = createKycRepository();

  return useMutation<KycRecord, AxiosError<{message: string}>, CreateKycParams>({
    mutationFn: (params) => repository.createKycRecord(params),

    onError: (error) => {
      message.error(error.message || "Failed to create KYC record");
      AnalyticsEvent("KYC Record", "KYC Record create fail");
    },

    onSuccess: () => {
      AnalyticsEvent("KYC Record", "KYC Record create success");
    },

    onSettled: () => {
      // Invalidate both new and legacy cache keys for backward compatibility
      queryClient.invalidateQueries({queryKey: KYC_QUERY_KEYS.status()});
      queryClient.invalidateQueries({queryKey: LEGACY_KYC_QUERY_KEY});
      // Invalidate profile status so kycVerified flag refreshes
      queryClient.invalidateQueries({queryKey: PROFILE_STATUS_QUERY_KEY});
    },
  });
};
